import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Heart, ShoppingCart, Star, Truck, Shield, RefreshCw, ChevronRight, Minus, Plus, Share2, ArrowLeft, ExternalLink } from 'lucide-react';
import StoreMessageBox from '../components/StoreMessageBox';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Separator } from '../components/ui/separator';
import ProductCard from '../components/ProductCard';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductDetailPage = () => {
  const { id, slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { addToCart } = useCart();

  const [product, setProduct] = useState(null);
  const [store, setStore] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [oemAlternatives, setOemAlternatives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);

  useEffect(() => {
    // Sayfa açıldığında en üste scroll
    window.scrollTo(0, 0);
    fetchProduct();
  }, [id, slug]);

  const [offers, setOffers] = useState([]);
  const [bestOffer, setBestOffer] = useState(null);
  const [selectedOffer, setSelectedOffer] = useState(null);

  const fetchProduct = async () => {
    setLoading(true);
    try {
      // Use marketplace endpoint to get product with all offers
      const [marketplaceRes, reviewsRes] = await Promise.all([
        slug 
        ? axios.get(`${API}/products/by-seo-slug/${encodeURIComponent(slug)}`)
        : axios.get(`${API}/marketplace/products/${id}`),
        axios.get(`${API}/products/${id}/reviews`)
      ]);
      
      setProduct(marketplaceRes.data.product);
      
      // Redirect old /products/:id URLs to new /urun/:slug SEO URLs
      if (id && !slug && marketplaceRes.data.product) {
        const p = marketplaceRes.data.product;
        const TR_MAP = {'ç':'c','Ç':'c','ğ':'g','Ğ':'g','ı':'i','İ':'i','ö':'o','Ö':'o','ş':'s','Ş':'s','ü':'u','Ü':'u','â':'a','Â':'a','î':'i','Î':'i','û':'u','Û':'u'};
        const sl = (t) => { if(!t) return ''; let s=t.toLowerCase(); for(const[k,v] of Object.entries(TR_MAP)) s=s.replaceAll(k,v); return s.replace(/[^a-z0-9\s-]/g,'').replace(/[\s_]+/g,'-').replace(/-+/g,'-').replace(/^-|-$/g,'').slice(0,120); };
        const seoSlug = [sl(p.brand),sl(p.oem_code),sl(p.name)].filter(Boolean).join('-').slice(0,150).replace(/-$/,'');
        if (seoSlug) navigate(`/urun/${seoSlug}`, { replace: true });
      }
      setOffers(marketplaceRes.data.offers || []);
      setOemAlternatives(marketplaceRes.data.oem_alternatives || []);
      setBestOffer(marketplaceRes.data.best_offer);
      setSelectedOffer(marketplaceRes.data.best_offer);
      setReviews(reviewsRes.data);

      // Set store from best offer
      if (marketplaceRes.data.best_offer) {
        setStore({
          id: marketplaceRes.data.best_offer.store_id,
          name: marketplaceRes.data.best_offer.store_name
        });
      }

      // Fetch related products from marketplace
      if (marketplaceRes.data.product.category_id) {
        const relatedRes = await axios.get(`${API}/marketplace/products?category=${marketplaceRes.data.product.category_id}&limit=6`);
        setRelatedProducts((relatedRes.data.products || []).filter(p => p.id !== id));
      }

      // Check if favorite
      if (isAuthenticated) {
        try {
          const favRes = await axios.get(`${API}/favorites/check/${id}`);
          setIsFavorite(favRes.data.is_favorite);
        } catch (e) {
          // Not a favorite
        }
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Ürün bulunamadı');
    } finally {
      setLoading(false);
    }
  };


// Misafir sepetine ekle (localStorage)
const addToGuestCart = (prod, qty, sellerOffer) => {
  const itemPrice = sellerOffer ? sellerOffer.price : (prod.display_price || prod.best_price || (prod.base_price ? prod.base_price * 1.40 : 0));
  const sellerId = sellerOffer ? sellerOffer.store_id : null;
  const sellerName = sellerOffer ? sellerOffer.store_name : null;
  const cartKey = sellerId ? `${prod.id}_${sellerId}` : prod.id;
  
  const guestCart = JSON.parse(localStorage.getItem('pazarlo_guest_cart') || '[]');
  const existing = guestCart.find(item => (item.cart_key || item.id) === cartKey);
  
  if (existing) {
    existing.quantity = Math.min(existing.quantity + qty, sellerOffer?.stock || prod.stock || 99);
  } else {
    guestCart.push({
      id: prod.id,
      cart_key: cartKey,
      name: prod.name,
      brand: prod.brand,
      oem_code: prod.oem_code,
      price: itemPrice,
      display_price: itemPrice,
      image_url: prod.image_url || prod.images?.[0],
      stock: sellerOffer?.stock || prod.stock || 99,
      quantity: qty,
      seller_id: sellerId,
      seller_name: sellerName,
      seller_listing_id: sellerOffer?.id || null
    });
  }
  
  localStorage.setItem('pazarlo_guest_cart', JSON.stringify(guestCart));
  return guestCart;
};

  const handleAddToCart = async () => {
    const activeOffer = selectedOffer || bestOffer;
    // Misafir için: localStorage'a ekle ve guest-checkout'a yönlendir
    if (!isAuthenticated) {
      addToGuestCart(product, quantity, activeOffer);
      toast.success(`Ürün sepete eklendi! (${activeOffer?.store_name || 'Pazarlo'})`, {
        action: {
          label: 'Sepete Git',
          onClick: () => navigate('/guest-checkout')
        }
      });
      return;
    }

    setAddingToCart(true);
    try {
      await addToCart(product.id, quantity, activeOffer?.store_id, activeOffer?.id, activeOffer?.price);
      toast.success(`Ürün sepete eklendi! (${activeOffer?.store_name || 'Pazarlo'})`);
    } catch (error) {
      toast.error('Ürün eklenemedi');
    } finally {
      setAddingToCart(false);
    }
  };

  const handleBuyNow = async () => {
    const activeOffer = selectedOffer || bestOffer;
    // Misafir için: localStorage'a ekle ve guest-checkout'a yönlendir
    if (!isAuthenticated) {
      addToGuestCart(product, quantity, activeOffer);
      navigate('/guest-checkout?cart=true');
      return;
    }

    // Kayıtlı kullanıcı: sepete ekle ve direkt checkout'a git
    try {
      await addToCart(product.id, quantity, activeOffer?.store_id, activeOffer?.id, activeOffer?.price);
      navigate('/checkout');
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const handleFavorite = async () => {
    if (!isAuthenticated) {
      toast.error('Favorilere eklemek için giriş yapın');
      return;
    }

    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/${product.id}`);
        setIsFavorite(false);
        toast.success('Favorilerden kaldırıldı');
      } else {
        await axios.post(`${API}/favorites/${product.id}`);
        setIsFavorite(true);
        toast.success('Favorilere eklendi');
      }
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  // Ürün fiyatını hesapla
  const displayPrice = product ? ((selectedOffer && selectedOffer.price) || product.display_price || product.best_price || (bestOffer && bestOffer.price) || (product.base_price ? product.base_price * 1.40 : 0)) : 0;

  const formatPrice = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(amount);
  };

  const renderStars = (rating, size = 'h-4 w-4') => {
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`${size} ${i < Math.round(rating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
      />
    ));
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="grid md:grid-cols-2 gap-8">
          <div className="aspect-square bg-slate-200 rounded-xl animate-pulse" />
          <div className="space-y-4">
            <div className="h-8 bg-slate-200 rounded animate-pulse" />
            <div className="h-6 bg-slate-200 rounded w-2/3 animate-pulse" />
            <div className="h-10 bg-slate-200 rounded w-1/3 animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <h2 className="text-2xl font-bold text-slate-600">Ürün bulunamadı</h2>
        <Link to="/products">
          <Button className="mt-4">Ürünlere Dön</Button>
        </Link>
      </div>
    );
  }

  // SEO için dinamik meta taglar
  const seoTitle = product ? `${product.name} | ${product.brand || 'Pazarlo'} - Oto Yedek Parça` : 'Ürün Detayı | Pazarlo';
  const seoDescription = product ? `${product.name} - ${product.brand || ''} marka oto yedek parça. ${product.oem_code ? 'OEM: ' + product.oem_code + '.' : ''} Stok: ${product.stock || 0} adet. Hızlı teslimat, güvenli alışveriş. Pazarlo.com'da en uygun fiyatlarla.` : '';
  const seoImage = product?.image_url || 'https://pazarlo.com/logo.png';
  const seoUrl = `https://pazarlo.com/products/${product?.id || ''}`;
  const seoPrice = displayPrice || 0;

  return (
    <>
      {product && (
        <Helmet>
          <title>{seoTitle}</title>
          <meta name="description" content={seoDescription} />
          <meta name="keywords" content={`${product.name}, ${product.brand || ''}, ${product.oem_code || ''}, oto yedek parça, araba parçası, ${product.category || ''}`} />
          <link rel="canonical" href={seoUrl} />
          
          {/* Open Graph */}
          <meta property="og:type" content="product" />
          <meta property="og:url" content={seoUrl} />
          <meta property="og:title" content={seoTitle} />
          <meta property="og:description" content={seoDescription} />
          <meta property="og:image" content={seoImage} />
          <meta property="og:locale" content="tr_TR" />
          <meta property="og:site_name" content="Pazarlo" />
          <meta property="product:price:amount" content={seoPrice.toString()} />
          <meta property="product:price:currency" content="TRY" />
          <meta property="product:availability" content={product.stock > 0 ? 'in stock' : 'out of stock'} />
          
          {/* Twitter */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={seoTitle} />
          <meta name="twitter:description" content={seoDescription} />
          <meta name="twitter:image" content={seoImage} />
          
          {/* JSON-LD Structured Data */}
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Product",
              "name": product.name,
              "description": seoDescription,
              "image": seoImage,
              "sku": product.sku,
              "mpn": product.oem_code || product.sku,
              "brand": {
                "@type": "Brand",
                "name": product.brand || "Pazarlo"
              },
              "offers": {
                "@type": "Offer",
                "url": seoUrl,
                "priceCurrency": "TRY",
                "price": seoPrice,
                "availability": product.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
                "seller": {
                  "@type": "Organization", 
                  "name": "Pazarlo"
                }
              }
            })}
          </script>
        </Helmet>
      )}
      
    <div className="min-h-screen bg-slate-50" data-testid="product-detail-page">
      <div className="container mx-auto px-4 py-6">
        {/* Mobile Back Button and Product Info Header */}
        <div className="md:hidden mb-4">
          <div className="flex items-center gap-3 mb-2">
            <button 
              onClick={() => window.history.back()}
              className="flex items-center justify-center w-10 h-10 bg-slate-100 rounded-full hover:bg-slate-200 transition-colors flex-shrink-0"
              data-testid="back-button"
            >
              <ArrowLeft className="h-5 w-5 text-slate-600" />
            </button>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-slate-500 mb-0.5">Ürün Detayı</div>
              <div className="text-sm font-medium text-slate-900 line-clamp-2 leading-tight">{product.name}</div>
            </div>
          </div>
          {/* Brand and OEM badges */}
          <div className="flex flex-wrap gap-2" style={{marginLeft: "52px"}}>
            {product.brand && (
              <span className="inline-flex items-center px-2.5 py-1 bg-orange-50 text-orange-700 text-xs font-medium rounded-full">
                {product.brand}
              </span>
            )}
            {(product.oem_code || product.oem_number) && (
              <span className="inline-flex items-center px-2.5 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full">
                OEM: {product.oem_code || product.oem_number}
              </span>
            )}
          </div>
        </div>

        {/* Desktop Breadcrumb */}
        <nav className="hidden md:flex flex-wrap items-center gap-1 md:gap-2 text-sm text-slate-500 mb-6">
          <Link to="/" className="hover:text-orange-500">Ana Sayfa</Link>
          <ChevronRight className="h-4 w-4" />
          <Link to="/products" className="hover:text-orange-500">Ürünler</Link>
          <ChevronRight className="h-4 w-4" />
          <span className="text-slate-900">{product.name}</span>
        </nav>

        <div className="bg-white rounded-xl border p-6 mb-8">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Images */}
            <div>
              <div className="aspect-square bg-white rounded-xl overflow-hidden mb-4 border">
                {product.image_url || (product.images && product.images.length > 0) ? (
                  <img
                    src={product.images && product.images.length > 1 ? product.images[selectedImage] : (product.image_url || product.images?.[0])}
                    alt={product.name}
                    className="w-full h-full object-contain p-4"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-400">
                    <ShoppingCart className="h-24 w-24" />
                  </div>
                )}
              </div>
              
              {(product.images && product.images.length > 1) && (
                <div className="flex gap-2 overflow-x-auto">
                  {product.images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedImage(idx)}
                      className={`w-20 h-20 rounded-lg overflow-hidden border-2 flex-shrink-0 ${
                        selectedImage === idx ? 'border-orange-500' : 'border-transparent'
                      }`}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div>
              {/* Title */}
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
                {product.name}
              </h1>

              {/* Rating */}
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center gap-1">
                  {renderStars(product.rating)}
                  <span className="text-sm font-medium ml-1">{(product.rating || 0).toFixed(1)}</span>
                </div>
                <span className="text-slate-400">|</span>
                <span className="text-sm text-slate-500">{product.review_count || 0} değerlendirme</span>
                <span className="text-slate-400">|</span>
                <span className="text-sm text-slate-500">{product.sold_count || 0} satış</span>
              </div>

              {/* Brand & OEM */}
              <div className="flex flex-wrap gap-2 mb-4">
                {product.brand && (
                  <Badge variant="outline">{product.brand}</Badge>
                )}
                {(product.oem_code || product.oem_number) && (
                  <Badge variant="outline">OEM: {product.oem_code || product.oem_number}</Badge>
                )}
                {product.condition && (
                  <Badge className={product.condition === 'new' ? 'bg-green-500' : 'bg-slate-500'}>
                    {product.condition === 'used' ? '2. El' : 'Sıfır'}
                  </Badge>
                )}
              </div>

              {/* Price - from selected offer */}
              {selectedOffer ? (
                <div className="bg-orange-50 rounded-xl p-4 mb-6">
                  <span className="text-3xl font-bold text-orange-600">
                    {formatPrice(selectedOffer.price)}
                  </span>
                  <div className="text-sm text-slate-600 mt-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span>Satıcı: {selectedOffer.store_slug ? <Link to={`/store/${selectedOffer.store_slug}`} className="font-medium text-orange-600 hover:underline inline-flex items-center gap-1">{selectedOffer.store_name} <ExternalLink className="h-3 w-3" /></Link> : <span className="font-medium">{selectedOffer.store_name}</span>}</span>
                      <div className="flex items-center gap-0.5">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className={`h-3 w-3 ${i < Math.round(selectedOffer.store_rating || 0) ? 'fill-yellow-400 text-yellow-400' : 'text-slate-300'}`} />
                        ))}
                        <span className="text-xs ml-1">
                          {(selectedOffer.store_rating || 0).toFixed(1)}
                          {selectedOffer.store_rating_count > 0 ? ` (${selectedOffer.store_rating_count} degerlendirme)` : ' (Yeni satici)'}
                        </span>
                      </div>
                    </div>
                    <div className="mt-1">
                      {offers.length > 1 && selectedOffer.id === bestOffer?.id && (
                        <span className="text-orange-500">+{offers.length - 1} satıcı daha</span>
                      )}
                      {selectedOffer.id !== bestOffer?.id && (
                        <button onClick={() => setSelectedOffer(bestOffer)} className="text-orange-500 hover:underline text-xs">
                          (En ucuz satıcıya dön)
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-slate-100 rounded-xl p-4 mb-6">
                  {displayPrice > 0 ? <span className="text-3xl font-bold text-orange-600">{formatPrice(displayPrice)}</span> : <span className="text-xl text-slate-500">Fiyat bilgisi yok</span>}
                  <p className="text-sm text-slate-400 mt-1">Henüz satıcı bulunmuyor</p>
                </div>
              )}

              {/* Stock - from selected offer */}
              {(selectedOffer && selectedOffer.stock > 0) || (bestOffer && bestOffer.stock > 0) || (product && product.stock > 0) ? (
                <p className="text-green-600 text-sm mb-4">
                  Stokta var 
                </p>
              ) : (
                <p className="text-red-500 text-sm mb-4">Stokta yok</p>
              )}

              {/* Quantity */}
              <div className="flex items-center gap-4 mb-6">
                <span className="text-sm font-medium">Adet:</span>
                <div className="flex items-center border rounded-lg">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    disabled={quantity <= 1}
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <input type="number" min="1" max={selectedOffer?.stock || bestOffer?.stock || product?.stock || 99} value={quantity} onChange={(e) => { const val = parseInt(e.target.value) || 1; setQuantity(Math.min(Math.max(1, val), bestOffer?.stock || product?.stock || 99)); }} className="w-16 text-center font-medium border-0 focus:ring-0 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setQuantity(Math.min(bestOffer?.stock || product?.stock || 99, quantity + 1))}
                    disabled={quantity >= (selectedOffer?.stock || bestOffer?.stock || product?.stock || 0)}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <Button
                  onClick={handleAddToCart}
                  disabled={addingToCart || ((!selectedOffer || selectedOffer.stock === 0) && (!bestOffer || bestOffer.stock === 0) && (!product || product.stock === 0))}
                  variant="outline"
                  className="flex-1 h-12 border-orange-500 text-orange-500 hover:bg-orange-50"
                  data-testid="add-to-cart-btn"
                >
                  <ShoppingCart className="h-5 w-5 mr-2" />
                  Sepete Ekle
                </Button>
                <Button
                  onClick={handleBuyNow}
                  disabled={((!selectedOffer || selectedOffer.stock === 0) && (!bestOffer || bestOffer.stock === 0) && (!product || product.stock === 0))}
                  className="flex-1 h-12 bg-orange-500 hover:bg-orange-600"
                  data-testid="buy-now-btn"
                >
                  Hemen Satın Al
                </Button>
              </div>

              {/* All Offers - Clickable Seller Selection */}
              {offers.length > 0 && (
                <div className="border rounded-xl p-4 mb-6">
                  <h3 className="font-semibold mb-3">Tüm Satıcılar ({offers.length})</h3>
                  <div className="space-y-2">
                    {offers.map((offer, idx) => {
                      const isSelected = selectedOffer?.id === offer.id;
                      const isCheapest = offer.price <= Math.min(...offers.map(o => o.price));
                      return (
                        <div
                          key={offer.id}
                          onClick={() => setSelectedOffer(offer)}
                          className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all duration-200 ${
                            isSelected
                              ? 'bg-orange-50 border-2 border-orange-400 shadow-sm'
                              : 'bg-slate-50 border-2 border-transparent hover:border-orange-200 hover:bg-orange-50/50'
                          }`}
                          data-testid={`seller-row-${idx}`}
                        >
                          <div className="flex items-center gap-2">
                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                              isSelected ? 'border-orange-500' : 'border-slate-300'
                            }`}>
                              {isSelected && <div className="w-2 h-2 rounded-full bg-orange-500" />}
                            </div>
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                {offer.store_slug ? <Link to={`/store/${offer.store_slug}`} className="font-medium text-orange-600 hover:underline inline-flex items-center gap-1" onClick={e => e.stopPropagation()}>{offer.store_name} <ExternalLink className="h-3 w-3" /></Link> : <span className="font-medium">{offer.store_name}</span>}
                                {idx === 0 && <Badge className="ml-1 bg-green-600 text-xs">Öne Çıkan</Badge>}
                                {isCheapest && <Badge className="ml-1 bg-orange-500 text-xs">En Ucuz</Badge>}
                                {isSelected && idx !== 0 && <Badge className="ml-1 bg-blue-500 text-xs">Secili</Badge>}
                              </div>
                              <div className="flex items-center gap-1 mt-0.5">
                                {[...Array(5)].map((_, i) => (
                                  <Star key={i} className={`h-3 w-3 ${i < Math.round(offer.store_rating || 0) ? 'fill-yellow-400 text-yellow-400' : 'text-slate-300'}`} />
                                ))}
                                <span className="text-xs text-slate-500 ml-1">
                                  {(offer.store_rating || 0).toFixed(1)}
                                  {offer.store_rating_count > 0 && ` (${offer.store_rating_count})`}
                                  {(!offer.store_rating_count || offer.store_rating_count === 0) && ' (Yeni)'}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className={`font-bold ${isSelected ? 'text-orange-600' : 'text-slate-700'}`}>{formatPrice(offer.price)}</span>
                            
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Secondary Actions */}
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  onClick={handleFavorite}
                  className={isFavorite ? 'text-red-500' : ''}
                  data-testid="favorite-btn"
                >
                  <Heart className={`h-5 w-5 mr-2 ${isFavorite ? 'fill-current' : ''}`} />
                  {isFavorite ? 'Favorilerde' : 'Favorilere Ekle'}
                </Button>
                <Button variant="ghost">
                  <Share2 className="h-5 w-5 mr-2" />
                  Paylaş
                </Button>
              </div>

              {/* Satıcıya Soru Sor */}
              {selectedOffer && selectedOffer.store_id && selectedOffer.store_id !== 'pazarlo' && (
                <div className="mt-3">
                  <StoreMessageBox storeId={selectedOffer.store_id} storeName={selectedOffer.store_name || 'Satici'} />
                </div>
              )}

              <Separator className="my-6" />

              {/* Trust Badges */}
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-3">
                  <Truck className="h-6 w-6 text-orange-500 mx-auto mb-1" />
                  <span className="text-xs text-slate-600">Hızlı Kargo</span>
                </div>
                <div className="p-3">
                  <Shield className="h-6 w-6 text-orange-500 mx-auto mb-1" />
                  <span className="text-xs text-slate-600">Güvenli Ödeme</span>
                </div>
                <div className="p-3">
                  <RefreshCw className="h-6 w-6 text-orange-500 mx-auto mb-1" />
                  <span className="text-xs text-slate-600">Kolay İade</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl border p-6 mb-8">
          <Tabs defaultValue="specs">
            <TabsList className="mb-6 flex overflow-x-auto whitespace-nowrap scrollbar-hide">
              <TabsTrigger value="specs" className="text-[11px] md:text-sm px-2 md:px-4">
                <span className="hidden md:inline">Teknik Özellikler</span>
                <span className="md:hidden">Özellikler</span>
              </TabsTrigger>
              <TabsTrigger value="description" className="text-[11px] md:text-sm px-2 md:px-4">
                <span className="hidden md:inline">Ürün Açıklaması</span>
                <span className="md:hidden">Açıklama</span>
              </TabsTrigger>
              <TabsTrigger value="reviews" className="text-[11px] md:text-sm px-2 md:px-4">
                <span className="hidden md:inline">Değerlendirmeler ({product.review_count})</span>
                <span className="md:hidden">Yorumlar ({product.review_count})</span>
              </TabsTrigger>
              {product.compatible_vehicles?.length > 0 && (
                <TabsTrigger value="compatibility" className="text-[11px] md:text-sm px-2 md:px-4">
                  <span className="hidden md:inline">Uyumlu Araçlar</span>
                  <span className="md:hidden">Araçlar</span>
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="specs">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Sol Kolon - Temel Bilgiler */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-slate-800 border-b pb-2">Ürün Bilgileri</h3>
                  <div className="space-y-3">
                    {product.brand && (
                      <div className="flex justify-between py-2 border-b border-slate-100">
                        <span className="text-slate-500">Marka</span>
                        <span className="font-medium text-slate-800">{product.brand}</span>
                      </div>
                    )}
                    {(product.oem_code || product.oem_number) && (
                      <div className="flex justify-between py-2 border-b border-slate-100">
                        <span className="text-slate-500">OEM / Parça No</span>
                        <span className="font-medium text-slate-800">{product.oem_code || product.oem_number}</span>
                      </div>
                    )}
                    {product.netsis_stok_kodu && (
                      <div className="flex justify-between py-2 border-b border-slate-100">
                        <span className="text-slate-500">Stok Kodu</span>
                        <span className="font-medium text-slate-800">{product.netsis_stok_kodu}</span>
                      </div>
                    )}
                    {product.category_name && (
                      <div className="flex justify-between py-2 border-b border-slate-100">
                        <span className="text-slate-500">Kategori</span>
                        <span className="font-medium text-slate-800">{product.category_name}</span>
                      </div>
                    )}
                    {product.netsis_grup && product.netsis_grup.trim() && (
                      <div className="flex justify-between py-2 border-b border-slate-100">
                        <span className="text-slate-500">Araç Grubu</span>
                        <span className="font-medium text-slate-800">{product.netsis_grup}</span>
                      </div>
                    )}
                    <div className="flex justify-between py-2 border-b border-slate-100">
                      <span className="text-slate-500">Durum</span>
                      <span className="font-medium text-green-600">
                        {product.condition === 'used' ? '2. El' : 'Sıfır'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Sağ Kolon - Ek Bilgiler */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-slate-800 border-b pb-2">Satış Bilgileri</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between py-2 border-b border-slate-100">
                      <span className="text-slate-500">Satış Fiyatı</span>
                      <span className="font-bold text-orange-600">{formatPrice(displayPrice)}</span>
                    </div>
                    {product.original_price && product.original_price > displayPrice && (
                      <div className="flex justify-between py-2 border-b border-slate-100">
                        <span className="text-slate-500">Liste Fiyatı</span>
                        <span className="font-medium text-slate-400 line-through">{formatPrice(product.original_price)}</span>
                      </div>
                    )}
                    <div className="flex justify-between py-2 border-b border-slate-100">
                      <span className="text-slate-500">Satış Adedi</span>
                      <span className="font-medium text-slate-800">{product.sold_count || 0} adet satıldı</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-100">
                      <span className="text-slate-500">Değerlendirme</span>
                      <div className="flex items-center gap-1">
                        <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                        <span className="font-medium">{product.rating?.toFixed(1) || '0.0'}</span>
                        <span className="text-slate-400 text-sm">({product.review_count || 0})</span>
                      </div>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-100">
                      <span className="text-slate-500">Stok Durumu</span>
                      <span className="font-medium text-green-600">{product.stock > 0 ? "Stokta var" : "Stokta yok"}</span>
                    </div>
                  </div>

                  {/* Specifications objesi varsa göster */}
                  {product.specifications && Object.keys(product.specifications).length > 0 && (
                    <>
                      <h3 className="text-lg font-semibold text-slate-800 border-b pb-2 mt-6">Teknik Detaylar</h3>
                      <div className="space-y-3">
                        {Object.entries(product.specifications).map(([key, value]) => (
                          <div key={key} className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">{key}</span>
                            <span className="font-medium text-slate-800">{value}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="description">
              <div className="prose max-w-none">
                <p className="text-slate-600 whitespace-pre-line">{product.description}</p>
              </div>
            </TabsContent>

            <TabsContent value="reviews">
              {reviews.length > 0 ? (
                <div className="space-y-4">
                  {reviews.map((review) => (
                    <div key={review.id} className="border-b pb-4">
                      <div className="flex items-center gap-3 mb-2">
                        <Avatar>
                          <AvatarFallback>{review.user_name?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{review.user_name}</p>
                          <div className="flex items-center gap-2">
                            <div className="flex">{renderStars(review.rating, 'h-3 w-3')}</div>
                            <span className="text-xs text-slate-500">
                              {new Date(review.created_at).toLocaleDateString('tr-TR')}
                            </span>
                          </div>
                        </div>
                      </div>
                      {review.comment && (
                        <p className="text-slate-600 ml-12">{review.comment}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">Henüz değerlendirme yok</p>
              )}
            </TabsContent>

            {product.compatible_vehicles?.length > 0 && (
              <TabsContent value="compatibility">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                  {product.compatible_vehicles.map((vehicle, idx) => (
                    <Badge key={idx} variant="outline" className="justify-center py-2">
                      {vehicle}
                    </Badge>
                  ))}
                </div>
              </TabsContent>
            )}
          </Tabs>
        </div>

        {/* OEM Alternatif Urunler */}
        {oemAlternatives.length > 0 && (
          <div className="mb-8" data-testid="oem-alternatives-section">
            <div className="flex items-center gap-3 mb-4">
              <RefreshCw className="w-5 h-5 text-orange-500" />
              <h2 className="text-xl font-bold text-slate-900">
                Alternatif Ürünler
                <span className="text-sm font-normal text-slate-500 ml-2">
                  ({oemAlternatives.length} ürün) • OEM: {product?.oem_code}
                </span>
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
              {oemAlternatives.map((alt) => (
                <ProductCard key={alt.id || alt.sku} product={alt} />
              ))}
            </div>
          </div>
        )}

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-bold text-slate-900 mb-4">Benzer Ürünler</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
              {relatedProducts.slice(0, 6).map((prod) => (
                <ProductCard key={prod.id} product={prod} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default ProductDetailPage;
